import { createContext } from 'react';
import {
  CognitoAccessToken,
  CognitoAuth,
  CognitoAuthSession
} from 'amazon-cognito-auth-js';
// Shared API base URL supports REACT_APP_API_URL overrides used in e2e/CI.
import { baseUrl } from './base-url';
import { consumePendingMcpConsent } from './mcp-consent';

declare global {
  interface Window {
    auth: {
      clientId: string;
      domain: string;
      poolId: string;
    };
  }
}

interface State {
  location?: string;
}

interface WellKnown {
  authProvider?: 'cognito' | 'google' | 'microsoft' | 'test';
  userPoolId?: string;
  clientId?: string;
  domain?: string;
  loginUrl?: string;
}

export interface TestAuthUser {
  id: string;
  email: string;
  name: string;
  groups: string[];
  role: 'administrator' | 'manager' | 'employee';
}

interface IdToken {
  auth_time: number;
  'cognito:groups': string[];
  'cognito:username': string;
  email: string;
  exp: number;
  iat: number;
  iss: string;
  sub: string;
  token_use: string;
}

let auth: CognitoAuth | undefined;
let testToken: string | undefined;
let orgAppToken: string | undefined;
let initialized = false;
let initializedOrganization: string | undefined;
let provider: 'cognito' | 'google' | 'microsoft' | 'test' = 'cognito';
let oidcLoginUrl: string | undefined;
let currentOrganization = '';
const isOidcProvider = () => provider === 'google' || provider === 'microsoft';
const getAuthTokenStorageKey = (organization: string) =>
  `spothr.authToken.${organization}`;
const getTestTokenStorageKey = (organization: string) =>
  `spothr.testToken.${organization}`;
const enableTestAuth =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_ENABLE_TEST_AUTH === 'true';

const decodeJwtPayload = (token: string): Record<string, unknown> => {
  // Test-token payload decoding is only used for local/e2e non-production flows.
  // The backend still validates the token for real API access.
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return {};
    }
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const requiredPadding = normalizedPayload.length % 4;
    const paddedPayload =
      requiredPadding === 0
        ? normalizedPayload
        : normalizedPayload + '='.repeat(4 - requiredPadding);
    return JSON.parse(atob(paddedPayload));
  } catch {
    return {};
  }
};

const isValidProvider = (
  value: unknown
): value is 'cognito' | 'google' | 'microsoft' | 'test' =>
  value === 'cognito' ||
  value === 'google' ||
  value === 'microsoft' ||
  value === 'test';

const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload.exp);
  if (!exp) {
    return true;
  }
  return exp * 1000 <= Date.now();
};

const getOrganizationFromPath = (): string | undefined => {
  const segments = window.location.pathname.split('/').filter(Boolean);

  if (segments[0] === 'callback') {
    return segments[1];
  }

  return segments[0];
};

const getActiveOrganization = (): string | undefined =>
  currentOrganization || initializedOrganization || getOrganizationFromPath();

const getStoredOrgAppToken = (organization: string): string | undefined => {
  const storedAuthToken = localStorage.getItem(
    getAuthTokenStorageKey(organization)
  );

  if (!storedAuthToken) {
    return undefined;
  }

  if (isTokenExpired(storedAuthToken)) {
    localStorage.removeItem(getAuthTokenStorageKey(organization));
    return undefined;
  }

  return storedAuthToken;
};

const getStoredTestToken = (organization: string): string | undefined => {
  const storedTestToken = localStorage.getItem(
    getTestTokenStorageKey(organization)
  );

  if (!storedTestToken) {
    return undefined;
  }

  if (isTokenExpired(storedTestToken)) {
    localStorage.removeItem(getTestTokenStorageKey(organization));
    return undefined;
  }

  return storedTestToken;
};

const getActiveTestToken = (): string | undefined => {
  if (!enableTestAuth || !testToken) {
    return undefined;
  }

  if (isTokenExpired(testToken)) {
    const organization = getActiveOrganization();
    if (organization) {
      localStorage.removeItem(getTestTokenStorageKey(organization));
    }
    testToken = undefined;
    return undefined;
  }

  return testToken;
};

const getActiveOrgAppToken = (): string | undefined => {
  const organization = getActiveOrganization();

  if (!organization) {
    return undefined;
  }

  return orgAppToken || getStoredOrgAppToken(organization);
};

const getActiveToken = (): string | undefined =>
  getActiveOrgAppToken() || getActiveTestToken();
const getActiveTokenPayload = (): Record<string, unknown> => {
  const token = getActiveToken();
  return token ? decodeJwtPayload(token) : {};
};

const getActiveTokenGroups = (): string[] => {
  const tokenPayload = getActiveTokenPayload();
  const cognitoGroups = tokenPayload['cognito:groups'];
  const directGroups = tokenPayload.groups;

  return (
    (Array.isArray(cognitoGroups) ? (cognitoGroups as string[]) : undefined) ||
    (Array.isArray(directGroups) ? (directGroups as string[]) : undefined) ||
    []
  );
};

async function init(organization: string) {
  if (initializedOrganization && initializedOrganization !== organization) {
    auth = undefined;
    testToken = undefined;
    orgAppToken = undefined;
    initialized = false;
    provider = 'cognito';
    oidcLoginUrl = undefined;
  }

  currentOrganization = organization;

  let wellKnown: WellKnown | undefined;
  try {
    const response = await fetch(`${baseUrl}/org/${organization}/wellknown`);
    if (response.ok) {
      wellKnown = (await response.json()) as WellKnown;
    }
  } catch {
    // Ignore and fall back to cached provider/default below.
  }

  const storedProvider = localStorage.getItem(
    `spothr.authProvider.${organization}`
  );
  const wellKnownProvider = wellKnown?.authProvider;
  provider = isValidProvider(wellKnownProvider)
    ? wellKnownProvider
    : isValidProvider(storedProvider)
    ? storedProvider
    : 'cognito';
  localStorage.setItem(`spothr.authProvider.${organization}`, provider);

  const storedOrgAppToken = getStoredOrgAppToken(organization);
  if (storedOrgAppToken) {
    orgAppToken = storedOrgAppToken;
    initialized = true;
    initializedOrganization = organization;
    return;
  }

  orgAppToken = undefined;

  if (enableTestAuth) {
    const storedTestToken = getStoredTestToken(organization);
    if (storedTestToken) {
      testToken = storedTestToken;
      initialized = true;
      initializedOrganization = organization;
      return;
    }

    testToken = undefined;
  }

  if (provider === 'test') {
    initialized = true;
    initializedOrganization = organization;
    return;
  }

  if (isOidcProvider()) {
    oidcLoginUrl =
      wellKnown?.loginUrl ||
      `${baseUrl}/org/${organization}/auth/${provider}/start`;
    initialized = true;
    initializedOrganization = organization;
    return;
  }

  if (!auth) {
    try {
      auth = new CognitoAuth({
        ClientId: wellKnown?.clientId || '',
        AppWebDomain: wellKnown?.domain || '',
        UserPoolId: wellKnown?.userPoolId || '',
        TokenScopesArray: ['openid', 'profile', 'email'],
        RedirectUriSignIn: `${window.location.origin}/callback/${organization}`,
        RedirectUriSignOut: `${window.location.origin}/signin`,
        IdentityProvider: 'COGNITO',
        AdvancedSecurityDataCollectionFlag: false
      });
      auth.userhandler = {
        onSuccess: (authSession: CognitoAuthSession) => {
          if (!auth) {
            throw new Error('Auth not loaded yet, call init first');
          }

          auth.setState(decodeURIComponent(authSession.getState()));

          // Password (cognito) logins complete here rather than via the org
          // token callback, so they bypass handleCallback's consent-resume. If
          // an MCP consent was pending, resume it on the workspace's consent
          // screen instead of dropping the user on the dashboard (mirrors the
          // token path in handleCallback/applyOrgToken).
          const pendingConsent = consumePendingMcpConsent(organization);
          if (pendingConsent) {
            window.location.href = `${window.location.origin}${pendingConsent}`;
            return;
          }

          const tryGetLocation = (a: CognitoAuth) => {
            try {
              const state = JSON.parse(atob(a.getState()));
              return state.location;
            } catch (err) {
              console.error(err);
              return window.location.href.includes('/callback/')
                ? `${window.location.origin}/${organization}`
                : window.location.href;
            }
          };
          window.location.href =
            tryGetLocation(auth) ?? `${window.location.origin}/${organization}`;
        },
        onFailure: (err: unknown) => {
          console.error('Error handling auth token update', err);

          if (!auth) {
            throw new Error('Auth not loaded yet, call init first');
          }

          auth.clearCachedTokensScopes();
          window.location.href = `${window.location.origin}/${organization}`;
        }
      };
      initialized = true;
      initializedOrganization = organization;
    } catch (e) {
      window.location.href = `${window.location.origin}/${organization}/notFound`;
    }
  }
}

// `skipChooser` starts the cognito hosted UI immediately instead of bouncing
// through the /signin chooser. Callers that have already established intent and
// the workspace (e.g. the MCP consent screen) use it so password users go
// straight to login — the same single-step experience OIDC already gets. It
// only affects cognito: OIDC always goes direct, and test has no hosted UI so
// it must still reach /signin to pick a user.
async function requestSignin(options?: { skipChooser?: boolean }) {
  if (isOidcProvider()) {
    window.location.href =
      oidcLoginUrl ||
      `${baseUrl}/org/${currentOrganization}/auth/${provider}/start`;
    return;
  }

  if (provider === 'test') {
    const signinUrl = new URL(`${window.location.origin}/signin`);
    if (currentOrganization) {
      signinUrl.searchParams.set('orgId', currentOrganization);
    }
    window.history.pushState(
      {},
      '',
      `${signinUrl.pathname}${signinUrl.search}`
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (!options?.skipChooser && searchParams.get('authFlow') !== 'cognito') {
    const signinUrl = new URL(`${window.location.origin}/signin`);
    if (currentOrganization) {
      signinUrl.searchParams.set('orgId', currentOrganization);
    }
    window.history.pushState(
      {},
      '',
      `${signinUrl.pathname}${signinUrl.search}`
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  if (!auth) {
    throw new Error('Auth not loaded yet, call init first');
  }

  const returnLocation = new URL(window.location.href);
  returnLocation.searchParams.delete('authFlow');

  auth.useCodeGrantFlow();
  auth.setState(btoa(JSON.stringify({ location: returnLocation.toString() })));
  auth.getSession();
}

function clearOrganizationAuthState(organization?: string) {
  if (organization) {
    localStorage.removeItem(`spothr.authToken.${organization}`);
    localStorage.removeItem(`spothr.testToken.${organization}`);
    localStorage.removeItem(`spothr.authProvider.${organization}`);
  }

  try {
    auth?.clearCachedTokensScopes();
  } catch {
    // Ignore cache clearing failures and continue logout flow.
  }

  auth = undefined;
  testToken = undefined;
  orgAppToken = undefined;
  initialized = false;
  initializedOrganization = undefined;
  provider = 'cognito';
  oidcLoginUrl = undefined;
  currentOrganization = '';
}

function logout() {
  const organization =
    currentOrganization || initializedOrganization || getOrganizationFromPath();

  if (provider === 'cognito' && auth) {
    const authInstance = auth;
    try {
      authInstance.signOut();
    } finally {
      clearOrganizationAuthState(organization);
    }
    return;
  }

  clearOrganizationAuthState(organization);
  window.location.href = `${window.location.origin}/signin`;
}

function handleCallback() {
  const searchParams = new URLSearchParams(window.location.search);
  const organization = getOrganizationFromPath() || currentOrganization;
  const token = searchParams.get('token');

  if (token && organization && organization !== 'platform') {
    const tokenPayload = decodeJwtPayload(token);
    provider = isValidProvider(tokenPayload.provider)
      ? tokenPayload.provider
      : 'google';
    currentOrganization = organization;
    initializedOrganization = organization;
    initialized = true;
    auth = undefined;
    orgAppToken = token;
    testToken = undefined;
    localStorage.removeItem(getTestTokenStorageKey(organization));
    localStorage.setItem(getAuthTokenStorageKey(organization), token);
    localStorage.setItem(`spothr.authProvider.${organization}`, provider);
    // If the backend auto-selected this single-workspace user (redirecting here
    // rather than to the platform picker) while an MCP consent was pending,
    // resume it instead of dropping them on the dashboard.
    const pendingConsent = consumePendingMcpConsent(organization);
    window.location.href = pendingConsent
      ? `${window.location.origin}${pendingConsent}`
      : `${window.location.origin}/${organization}`;
    return;
  }

  if (provider === 'google' || provider === 'microsoft') {
    const hasError = !!searchParams.get('error');
    if (hasError) {
      const callbackOrganization = organization || currentOrganization;
      window.location.href = `${window.location.origin}/${callbackOrganization}`;
      return;
    }
  }

  if (!auth) {
    throw new Error('Auth not loaded yet, call init first');
  }
  auth.parseCognitoWebResponse(window.location.href);
}

async function getTestUsers(organization: string): Promise<TestAuthUser[]> {
  const response = await fetch(
    `${baseUrl}/org/${organization}/auth/test-users`
  );

  if (!response.ok) {
    throw new Error('Test users not available');
  }

  return (await response.json()) as TestAuthUser[];
}

async function loginWithTestUser(
  organization: string,
  userId?: string
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/org/${organization}/auth/test-login`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userId ? { userId } : {})
    }
  );

  if (!response.ok) {
    throw new Error('Test login failed');
  }

  const data = (await response.json()) as { token?: string };

  if (!data.token) {
    throw new Error('Test login token missing');
  }

  auth = undefined;
  orgAppToken = undefined;
  provider = 'test';
  currentOrganization = organization;
  initializedOrganization = organization;
  initialized = true;
  testToken = data.token;
  localStorage.removeItem(getAuthTokenStorageKey(organization));
  localStorage.setItem(getTestTokenStorageKey(organization), data.token);
  localStorage.setItem(`spothr.authProvider.${organization}`, 'test');
}

export const AuthContext = createContext<{
  isSignedIn: () => boolean;
  requestSignin: (options?: { skipChooser?: boolean }) => unknown;
  handleCallback: () => void;
  getState: () => State;
  getToken: () => CognitoAccessToken;
  isInitialized: () => boolean;
  init: (organization: string) => Promise<void>;
  getCurrentUser: () => string;
  isAdministrator: () => boolean;
  getGroups: () => string[];
  logout: () => void;
  getTestUsers: (organization: string) => Promise<TestAuthUser[]>;
  loginWithTestUser: (organization: string, userId?: string) => Promise<void>;
}>({
  init,
  isInitialized: () => {
    const organizationFromPath = getOrganizationFromPath();
    if (
      organizationFromPath &&
      initializedOrganization &&
      initializedOrganization !== organizationFromPath
    ) {
      return false;
    }
    return initialized || !!auth || !!testToken || !!orgAppToken;
  },
  isSignedIn: () => {
    if (getActiveToken()) {
      return true;
    }

    if (isOidcProvider() || provider === 'test') {
      return false;
    }

    if (!auth) {
      throw new Error('Auth not loaded yet, call init first');
    }

    return auth.isUserSignedIn();
  },
  requestSignin,
  handleCallback,
  getState: () => {
    if (isOidcProvider() || provider === 'test') {
      return {};
    }

    if (!auth) {
      throw new Error('Auth not loaded yet, call init first');
    }

    return (auth.getState() ? JSON.parse(atob(auth.getState())) : {}) as State;
  },
  getToken: () => {
    const token = getActiveToken();
    if (token) {
      return {
        getJwtToken: () => token
      } as CognitoAccessToken;
    }

    if (isOidcProvider() || provider === 'test') {
      throw new Error('Auth token not available');
    }

    if (!auth) {
      throw new Error('Auth not loaded yet, call init first');
    }
    return auth.getCachedSession().getAccessToken();
  },
  getCurrentUser: () => {
    if (getActiveToken()) {
      const tokenPayload = getActiveTokenPayload();
      return (
        (tokenPayload.userId as string) ||
        (tokenPayload.sub as string) ||
        (tokenPayload.email as string) ||
        ''
      );
    }

    if (isOidcProvider() || provider === 'test') {
      return '';
    }

    if (!auth) {
      throw new Error('Auth not loaded yet, call init first');
    }
    return auth.getCurrentUser();
  },
  isAdministrator: () => {
    if (getActiveToken()) {
      const groups = getActiveTokenGroups();
      return groups.includes('administrators');
    }

    if (isOidcProvider() || provider === 'test') {
      return false;
    }

    if (!auth) {
      throw new Error('Auth not loaded yet, call init first');
    }
    const idToken = auth
      .getCachedSession()
      .getIdToken()
      .decodePayload();
    const groups = (idToken as IdToken)['cognito:groups'] as string[];
    return !!groups?.includes('administrators');
  },
  logout,
  getGroups: () => {
    if (getActiveToken()) {
      return getActiveTokenGroups();
    }

    if (isOidcProvider() || provider === 'test') {
      return [];
    }

    if (!auth) {
      throw new Error('Auth not loaded yet, call init first');
    }
    const idToken = auth
      .getCachedSession()
      .getIdToken()
      .decodePayload();
    const groups = (idToken as IdToken)['cognito:groups'] as string[];
    return groups || [];
  },
  getTestUsers,
  loginWithTestUser
});
